import { IconButton } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { Dataset } from "@/api";
import type { LoadState } from "@/components/dataset/loadState";
import { ChevronRightIcon } from "@/components/icons";
import { ChartCard } from "@/components/metric";
import { HatListView } from "@/components/table/HatListView";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { DatasetListRow } from "./DatasetListRow";

/**
 * 분량 상위 데이터셋 카드. 총 수집 길이 기준 상위 5건을 표시한다.
 *
 * 최근 등록 카드와 같은 관례(행 클릭 = 상세 창, `HatListView`)를 쓴다. 모집단은 "길이가 측정된
 * available" 이다. 그래서 순위 기준인 총 길이를 행 끝 눈금의 맨 위에 강조하고 총 용량을 그 아래에
 * 둔다. 등록일은 표시하지 않는다(분량 순위와 관계없다). 순위 배지도 없다. 행 순서가 곧 순위다.
 */
export function TopDurationDatasetsCard({
  state,
  onRetry,
  onThumbnailStale,
}: {
  state: LoadState<readonly Dataset[]>;
  onRetry: () => void;
  /** 섬네일 presigned URL 만료 신호. 페이지가 디바운서로 감싼 재조회 함수를 전달한다. */
  onThumbnailStale: () => void;
}): ReactNode {
  return (
    <ChartCard
      title="분량 상위"
      // 부제는 목록을 읽는 방법이다. 그래서 목록이 있을 때만 표시한다.
      subtitle={
        state.status === "success"
          ? "길이가 측정된 Dataset 중 총 수집 길이 상위 5건입니다. 행을 누르면 상세 창이 열립니다."
          : undefined
      }
      loading={
        state.status === "success" || state.status === "error"
          ? undefined
          : "분량 상위 Dataset을 불러오고 있습니다."
      }
      error={state.status === "error" ? { message: state.message, onRetry } : undefined}
      empty={
        state.status === "success" && state.data.length === 0
          ? "아직 길이가 측정된 Dataset이 없습니다."
          : undefined
      }
    >
      {state.status === "success" ? (
        <HatListView
          ariaLabel="분량 상위 Dataset"
          items={state.data}
          getKey={(dataset) => dataset.uuid}
          // 선택 상태와 화살표 탐색을 끄는 이유는 최근 등록 카드와 같다. 행 활성화가 곧 창 열기다.
          selectedKey={null}
          keyboardNavigation={false}
          onSelect={openDetailWindow}
          // 행 콘텐츠와 액션은 한 descriptor 로 전달한다. c6 ListView 가 액션을 primary
          // 버튼의 형제 셀로 렌더링해 행 선택과 이벤트를 분리한다.
          renderItem={(dataset) => ({
            content: (
              <DatasetListRow
                dataset={dataset}
                mode="duration"
                onThumbnailError={onThumbnailStale}
              />
            ),
            actions: (
              <IconButton
                size="sm"
                variant="ghost"
                border={false}
                aria-label={`${dataset.display_name} 상세 창 열기`}
                onClick={() => openDetailWindow(dataset)}
              >
                <ChevronRightIcon />
              </IconButton>
            ),
          })}
        />
      ) : null}
    </ChartCard>
  );
}

function openDetailWindow(dataset: Dataset): void {
  openDatasetDetailWindow({ datasetUuid: dataset.uuid, displayName: dataset.display_name });
}
